import { useCallback, useEffect, useRef, useState } from 'react'
import cytoscape from 'cytoscape'
import fcose from 'cytoscape-fcose'
import type { Core, NodeSingular, EdgeSingular, EventObject } from 'cytoscape'
import { useAppStore } from '@/store'
import { type Edge, type Node, parseName } from '@/service/graph'
import { useTranslation } from '@/i18n'
import { Loader2, Download, Search } from 'lucide-react'
import { getNodeIconUri } from '@/utils/nodeIcon.tsx'
import { colors } from '@/styles/colors'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import GraphSearch, { SEARCH_ZOOM_MIN, SEARCH_ZOOM_MAX } from './GraphSearch'
import { useUIv2 } from '@/hooks/useUIv2'
import { drawingConnectorClasses } from './connectorPortal'

// Constants
const COSE_LAYOUT_CONFIG = {
  name: 'cose' as const,
  padding: 50,
  animate: false,
  nodeOverlap: 8, // Prevent nodes from overlapping (lower value = less overlap)
  idealEdgeLength: () => 100,
  edgeElasticity: () => 0.9,
  randomize: false,
}

// fcose lays out the read-only KG preview (hundreds of positionless nodes) in
// ~2.3s vs ~6s for cose, which froze the tab. Cast because fcose isn't part of
// cytoscape's built-in LayoutOptions union.
const FCOSE_LAYOUT_CONFIG = {
  name: 'fcose',
  animate: false,
  padding: 50,
  idealEdgeLength: 100,
} as unknown as cytoscape.LayoutOptions

// Register fcose on first mount rather than at module load, so importing this
// file stays side-effect free.
let fcoseRegistered = false
function registerFcoseOnce(): void {
  if (fcoseRegistered) return
  cytoscape.use(fcose)
  fcoseRegistered = true
}

// fcose's only nondeterminism is its random initial placement, and it has no
// seed option, so the preview reshuffled on every open. Seed Math.random around
// the layout so it renders the same arrangement each time. run() is synchronous
// (animate:false), so the override never overlaps other code.
const KG_PREVIEW_LAYOUT_SEED = 1
function seededRandom(seed: number): () => number {
  // Standard LCG — only needs to be repeatable, not cryptographic.
  let state = seed
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff
    return state / 0x7fffffff
  }
}
function runFcoseLayout(cy: Core | null): void {
  if (!cy) return
  const originalRandom = Math.random
  Math.random = seededRandom(KG_PREVIEW_LAYOUT_SEED)
  try {
    cy.layout(FCOSE_LAYOUT_CONFIG).run()
  } finally {
    Math.random = originalRandom
  }
}

const LARGE_GRAPH_THRESHOLD = 20 // Nodes count to trigger loader
const BATCH_SIZE = 5 // Nodes per batch for large updates
// Cytoscape logs a warning when wheelSensitivity differs from the default (1.0).
// We accept this because the default is slightly aggressive for trackpads — but
// 0.3 made zooming feel glacial (endless scrolling to get anywhere). 0.8 keeps
// near-default responsiveness with a small softening.
const WHEEL_SENSITIVITY = 0.8

// Shared canvas context for measuring label text width
const _labelCtx = (() => {
  const c = document.createElement('canvas')
  return c.getContext('2d')
})()
const measureLabelWidth = (text: string): number => {
  if (!_labelCtx) return text.length * 8
  _labelCtx.font = '600 14px Inter, sans-serif'
  return _labelCtx.measureText(text).width
}
// Node layout constants: [16px left pad] [20px icon] [8px gap] [text] [16px right pad]
const NODE_ICON_AREA = 44 // 16 + 20 + 8
const NODE_RIGHT_PAD = 16
const NODE_MIN_WIDTH = 80

interface KnowledgeGraphProps {
  /**
   * When provided (V2 sub-navbar drives search), KnowledgeGraph runs the
   * same cytoscape highlight + center logic GraphSearch does, but driven by
   * this term instead of an internal modal. Match info flows back through
   * onSearchMatchInfo; navigation is exposed via the two refs.
   */
  externalSearchTerm?: string
  onSearchMatchInfo?: (info: { total: number; current: number }) => void
  onSearchNext?: React.MutableRefObject<(() => void) | null>
  onSearchPrevious?: React.MutableRefObject<(() => void) | null>
  /** Render this graph instead of the global store (e.g. KG preview). */
  graph?: { nodes: Map<string, Node>; edges: Map<string, Edge> }
  /** Hide edge create/delete controls and block store mutations. */
  readOnly?: boolean
  /** Tap handler for drawing_to_drawing connector portal nodes. */
  onDrawingConnectorClick?: (label: string, extraProperties?: Record<string, string>) => void
}

export default function KnowledgeGraph({
  externalSearchTerm,
  onSearchMatchInfo,
  onSearchNext,
  onSearchPrevious,
  graph,
  readOnly = false,
  onDrawingConnectorClick,
}: KnowledgeGraphProps = {}) {
  const isV2 = useUIv2()
  const { t } = useTranslation()
  const cyRef = useRef<HTMLDivElement>(null)
  const cyInstance = useRef<Core | null>(null)
  const [isCreatingEdge, setIsCreatingEdge] = useState(false)
  const [isDeletingEdge, setIsDeletingEdge] = useState(false)
  const [sourceNode, setSourceNode] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const prevStateRef = useRef<{ nodeIds: Set<string>, edgeIds: Set<string> }>({
    nodeIds: new Set(),
    edgeIds: new Set(),
  })
  const batchTimeoutRef = useRef<number | null>(null)
  const hasRenderedInitialData = useRef(false)
  const { currentState, classRecords, addEdge, deleteEdgeById } =
    useAppStore()
  // Render from an external graph (KG preview) when provided, else the store.
  const sourceNodes = graph?.nodes ?? currentState.nodes
  const sourceEdges = graph?.edges ?? currentState.edges

  // Function to download graph as image
  const downloadGraphImage = async () => {
    if (!cyInstance.current) {
      toast.error(t('data_catalog:graph_editor.toasts.graph_not_ready_download'))
      return
    }

    try {
      // Wait a brief moment to ensure rendering is complete
      await new Promise(resolve => setTimeout(resolve, 100))

      // Get the graph as base64 PNG (more reliable than blob)
      const png64 = cyInstance.current.png({
        output: 'base64',
        bg: 'white',
        full: true,
        scale: 2, // Good balance between quality and file size
        maxWidth: 4000,
        maxHeight: 4000,
      })

      // Convert base64 to blob
      const base64Data = png64.replace(/^data:image\/png;base64,/, '')
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'image/png' })

      // Create download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
      link.download = `graph-preview-${timestamp}.png`
      link.href = url
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Cleanup after a delay to ensure download starts
      setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 100)
      
      toast.success(t('data_catalog:graph_editor.toasts.graph_image_downloaded'))
    } catch (error) {
      console.error('Error downloading graph image:', error)
      toast.error(t('data_catalog:graph_editor.toasts.graph_image_download_failed', { error: error instanceof Error ? error.message : 'Unknown error' }))
    }
  }

  // Separate effect for initializing Cytoscape
  useEffect(() => {
    if (!cyRef.current) return
    registerFcoseOnce()

    try {
      cyInstance.current = cytoscape({
        container: cyRef.current,
        elements: {
          nodes: [],
          edges: [],
        },
        style: [
          {
            selector: 'core',
            style: {
              'z-index-compare': 'manual',
            },
          },
          {
            selector: 'node',
            style: isV2 ? {
              // V2: white card nodes with icon left + label right
              'background-color': '#ffffff',
              'background-image': (ele: NodeSingular) => {
                const uri = getNodeIconUri(ele.id(), ele.data('name'), ele.data('type'))
                return `url(${uri})`
              },
              'background-fit': 'none',
              'background-clip': 'node',
              'background-repeat': 'no-repeat',
              'background-width': 20,
              'background-height': 20,
              'background-position-x': '16px',
              'background-position-y': '50%',
              'background-image-opacity': 0.85,
              label: 'data(label)',
              'text-wrap': 'ellipsis',
              'text-max-width': '160px',
              'font-size': '14px',
              'font-weight': 600,
              'text-valign': 'center',
              'text-halign': 'center',
              'text-margin-x': (ele: NodeSingular) => {
                // Shift text right so it starts after the icon area.
                // With center alignment, margin = iconArea/2 - rightPad/2
                const label = ele.data('label') || ''
                const textW = Math.min(measureLabelWidth(label), 160)
                const nodeW = Math.max(NODE_MIN_WIDTH, NODE_ICON_AREA + textW + NODE_RIGHT_PAD)
                // text-center is at nodeW/2; we want text-left at NODE_ICON_AREA
                // text-left = nodeW/2 + margin - textW/2 = NODE_ICON_AREA
                return NODE_ICON_AREA - nodeW / 2 + textW / 2
              },
              padding: '0px',
              width: (ele: NodeSingular) => {
                const label = ele.data('label') || ''
                const textW = Math.min(measureLabelWidth(label), 160)
                return Math.max(NODE_MIN_WIDTH, NODE_ICON_AREA + textW + NODE_RIGHT_PAD)
              },
              height: 44,
              display: 'element',
              shape: 'roundrectangle',
              'border-width': 1,
              'border-color': '#e5e5e5',
              'border-opacity': 1,
              color: '#0a0a0a',
              'z-index': 0,
            } : {
              // Legacy styling
              'background-color': (ele: NodeSingular) => {
                const type = (ele.data('type') as string | undefined)?.toLowerCase()
                if (type === 'connection') {
                  return colors.blue[200]
                }
                const name = ele.data('name')
                return classRecords[name]?.color || colors.gray[300]
              },
              'background-image': (ele: NodeSingular) => {
                const uri = getNodeIconUri(ele.id(), ele.data('name'), ele.data('type'))
                return `url(${uri})`
              },
              'background-fit': 'none',
              'background-clip': 'node',
              'background-repeat': 'no-repeat',
              'background-width': 16,
              'background-height': 16,
              'background-position-x': '50%',
              'background-position-y': '50%',
              'background-image-opacity': 0.9,
              label: 'data(label)',
              'text-wrap': 'wrap',
              'text-max-width': '160px',
              'font-size': '10px',
              'text-valign': 'top',
              'text-halign': 'center',
              'text-margin-x': 0,
              'text-margin-y': (ele: NodeSingular) => {
                const label = (ele.data('label') as string | undefined) || ''
                return label.trim() ? 42 : 0
              },
              'text-background-color': colors.gray[100],
              'text-background-opacity': 1,
              'text-background-padding': '2px 4px',
              padding: '2px',
              width: 24,
              height: 24,
              display: 'element',
              shape: 'roundrectangle',
              'border-width': 2,
              'border-color': '#fff',
              'border-opacity': 1,
              color: '#000',
              'z-index': 0,
            },
          },
          {
            selector: 'node.source-node',
            style: {
              'border-width': 4,
              'border-color': '#4CAF50',
              'border-opacity': 1,
            },
          },
          {
            selector: 'node.search-match',
            style: {
              'border-width': 4,
              'border-color': colors.amber[500],
              'border-opacity': 1,
            },
          },
          {
            selector: 'node.search-current',
            style: {
              'border-width': 6,
              'border-color': colors.blue[600],
              'border-opacity': 1,
            },
          },
          {
            // Equipment wired in the drawing but unreachable in the canonical layer.
            selector: 'node.orphaned',
            style: {
              'background-color': '#ffffff',
              'border-width': 3,
              'border-color': '#dc2626',
              'border-opacity': 1,
            },
          },
          {
            // Node with no edges in the sheet: the KG dropped its connections.
            selector: 'node.disconnected',
            style: {
              'background-color': '#ffffff',
              'border-width': 3,
              'border-color': '#c084fc',
              'border-opacity': 1,
            },
          },
          {
            // Drawing-to-drawing connector: a portal to another sheet.
            selector: 'node.portal',
            style: {
              'border-width': 3,
              'border-style': 'dashed',
              'border-color': colors.amber[500],
              'border-opacity': 1,
            },
          },
          {
            // Backend-linked portal — tap navigates to the connected sheet.
            selector: 'node.portal-linked',
            style: {
              'border-style': 'solid',
              'border-color': colors.blue[600],
            },
          },
          {
            selector: 'edge',
            style: {
              width: isV2 ? 1.5 : 1,
              opacity: isV2 ? 0.6 : 1,
              'line-cap': 'round',
              'line-color': isV2 ? '#a3a3a3' : colors.gray[300],
              'underlay-color': colors.gray[300],
              'underlay-opacity': 0,
              'underlay-padding': 0,
              'target-arrow-color': isV2 ? '#a3a3a3' : colors.gray[300],
              'target-arrow-shape': 'none',
              'curve-style': isV2 ? 'bezier' : 'straight',
              'z-index': 10,
            },
          },
          {
            selector: 'edge.flows-to',
            style: {
              width: 2,
              'line-color': '#2563eb',
              'target-arrow-color': '#2563eb',
              'target-arrow-shape': 'triangle',
              'arrow-scale': 1.2,
              'curve-style': 'straight',
              'z-index': 20,
            },
          },
        ],
        hideEdgesOnViewport: false,
        hideLabelsOnViewport: false,
        motionBlur: false,
        textureOnViewport: false,
        pixelRatio: 'auto',
        layout: COSE_LAYOUT_CONFIG,
        wheelSensitivity: WHEEL_SENSITIVITY,
        userZoomingEnabled: true,
        userPanningEnabled: true,
        boxSelectionEnabled: false,
      })

      // Don't remove loading overlay yet - wait for initial data to be rendered
      // The loading state will be cleared after the first data update completes
    } catch (error) {
      console.error('Error creating Cytoscape instance:', error)
      setIsLoading(false)
    }

    return () => {
      if (cyInstance.current) {
        cyInstance.current.destroy()
        cyInstance.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount, classRecords accessed via closure

  // ─── External-search mode (V2 sub-navbar) ──────────────────────────
  // When externalSearchTerm is provided, KnowledgeGraph drives cytoscape
  // highlighting + centering directly (the same logic GraphSearch uses
  // internally) instead of going through the in-place search modal.
  const isExternalSearch = externalSearchTerm !== undefined

  // Keyboard shortcut: Cmd+F / Ctrl+F to open search. When the V2 sub-navbar
  // owns search, it handles the shortcut and no floating bar opens.
  useEffect(() => {
    if (isExternalSearch) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        setIsSearchOpen(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isExternalSearch])
  const [extMatchIds, setExtMatchIds] = useState<string[]>([])
  const [extMatchIdx, setExtMatchIdx] = useState(0)

  // Compute matches from the external term + current node map.
  useEffect(() => {
    if (!isExternalSearch) return
    const term = (externalSearchTerm ?? '').trim().toLowerCase()
    if (!term) {
      setExtMatchIds((prev) => (prev.length === 0 ? prev : []))
      return
    }
    const matches: string[] = []
    sourceNodes.forEach((node, nodeId) => {
      let searchable = ''
      if (node.type === 'symbol') {
        const sym = node as Extract<Node, { type: 'symbol' }>
        const nameToUse = sym.symbol_type && sym.symbol_type !== 'null' ? sym.symbol_type : sym.name
        const { label, category } = parseName(nameToUse)
        // Include the PIC001 classification + user label so a manually-classified
        // object (e.g. a traced "Pit" tank) is findable by its Type (ENG-2582).
        const classification = `${sym.pnid_level_i ?? ''} ${sym.pnid_level_ii ?? ''} ${sym.pnid_level_iii ?? ''} ${sym.label ?? ''}`
        searchable = `${nameToUse} ${label} ${category} ${classification}`.toLowerCase()
      } else if (node.type === 'tag') {
        const tag = node as Extract<Node, { type: 'tag' }>
        searchable = `${tag.content} ${tag.pnid_level_i ?? ''} ${tag.pnid_level_ii ?? ''} ${tag.pnid_level_iii ?? ''} ${tag.label ?? ''}`.toLowerCase()
      } else {
        return // connections aren't searchable
      }
      if (searchable.includes(term)) matches.push(nodeId)
    })
    setExtMatchIds((prev) => {
      if (prev.length === matches.length && prev.every((id, i) => id === matches[i])) return prev
      return matches
    })
  }, [isExternalSearch, externalSearchTerm, sourceNodes])

  // Reset the current index when the term changes.
  useEffect(() => {
    if (!isExternalSearch) return
    setExtMatchIdx(0)
  }, [isExternalSearch, externalSearchTerm])

  // Apply highlight classes + pan-to-current whenever the matches or
  // current index change.
  useEffect(() => {
    if (!isExternalSearch) return
    const cy = cyInstance.current
    if (!cy) return
    cy.nodes().removeClass('search-match search-current')
    if (extMatchIds.length === 0) return
    const safeIdx = Math.min(extMatchIdx, extMatchIds.length - 1)
    const currentId = extMatchIds[safeIdx]
    extMatchIds.forEach((id) => cy.$id(id).addClass('search-match'))
    cy.$id(currentId).addClass('search-current')
    cy.stop(true, false) // interrupt any in-flight pan so taps don't queue
    cy.animate({
      center: { eles: cy.$id(currentId) },
      zoom: Math.min(Math.max(cy.zoom(), SEARCH_ZOOM_MIN), SEARCH_ZOOM_MAX),
      duration: 300,
    })
  }, [isExternalSearch, extMatchIds, extMatchIdx])

  // Forward match info to the sub-navbar's "N/M" readout.
  useEffect(() => {
    if (!isExternalSearch) return
    onSearchMatchInfo?.({ total: extMatchIds.length, current: extMatchIdx })
  }, [isExternalSearch, extMatchIds, extMatchIdx, onSearchMatchInfo])

  // Wire next/prev navigation into the parent-owned refs.
  const extGoToNext = useCallback(() => {
    if (extMatchIds.length === 0) return
    setExtMatchIdx((i) => (i + 1) % extMatchIds.length)
  }, [extMatchIds.length])
  const extGoToPrev = useCallback(() => {
    if (extMatchIds.length === 0) return
    setExtMatchIdx((i) => (i - 1 + extMatchIds.length) % extMatchIds.length)
  }, [extMatchIds.length])
  useEffect(() => {
    if (!isExternalSearch) return
    if (onSearchNext) onSearchNext.current = extGoToNext
    if (onSearchPrevious) onSearchPrevious.current = extGoToPrev
  }, [isExternalSearch, extGoToNext, extGoToPrev, onSearchNext, onSearchPrevious])

  // Listen for download event from V2 sub-navbar
  useEffect(() => {
    const handleDownload = () => { downloadGraphImage() }
    window.addEventListener('graph-download', handleDownload)
    return () => window.removeEventListener('graph-download', handleDownload)
  }, [])

  // Effect for event listeners
  useEffect(() => {
    if (!cyInstance.current) return

    // Remove existing listeners
    cyInstance.current.removeAllListeners()

    // During active zoom/pan, simplify edges to avoid aliasing/flicker; restore on idle
    let zoomDebounce: number | null = null
    const applyZoomMode = (active: boolean) => {
      if (!cyInstance.current) return
      cyInstance.current.batch(() => {
        const edges = cyInstance.current!.edges()
        if (active) {
          edges.style('width', '0.9')
        } else {
          edges.style('width', '1')
        }
      })
    }
    cyInstance.current.on('zoom pan', () => {
      applyZoomMode(true)
      if (zoomDebounce !== null) window.clearTimeout(zoomDebounce)
      zoomDebounce = window.setTimeout(() => applyZoomMode(false), 120)
    })

    const rerender = () => {
      // Only add edges whose source and target nodes still exist
      const nodeExists = (nodeId: string) => sourceNodes.has(nodeId)
      const edges = Array.from(sourceEdges.entries())
        .filter(([, edge]) => nodeExists(edge.fromNodeId) && nodeExists(edge.toNodeId))
        .map(([id, edge]) => ({
          data: {
            id,
            source: edge.fromNodeId,
            target: edge.toNodeId,
            relation: edge.relation || '',
          },
          classes: edge.relation === 'flows_to' ? 'flows-to' : '',
        }))

      // Remove existing edges and re-add filtered edges
      cyInstance.current?.edges().remove()
      if (edges.length > 0) {
        cyInstance.current?.add(edges)
      }
    }

    // Add edge deletion listener
    cyInstance.current.on('tap', 'edge', (evt: EventObject) => {
      if (readOnly) return
      const edge = evt.target as EdgeSingular
      if (isDeletingEdge) {
        deleteEdgeById(edge.id())
        rerender()
      }
    })

    // Portal navigation: tap a backend-LINKED D2D connector to jump to the
    // connected sheet. Scoped to 'node.portal-linked' (not 'node.portal') so
    // tapping an unmatched/dashed connector — which has no destination — is a
    // no-op with no misleading silent click. Works in read-only previews too;
    // edit modes keep priority.
    cyInstance.current.on('tap', 'node.portal-linked', (evt: EventObject) => {
      if (isCreatingEdge || isDeletingEdge || !onDrawingConnectorClick) return
      const node = evt.target as NodeSingular
      const storeNode = sourceNodes.get(node.id())
      const label = (storeNode?.label ?? '').trim() || String(node.data('label') ?? '')
      onDrawingConnectorClick(label, storeNode?.extraProperties)
    })

    // Add node tap listener
    cyInstance.current.on('tap', 'node', (evt: EventObject) => {
      if (readOnly) return
      const node = evt.target as NodeSingular
      if (isCreatingEdge) {
        if (!sourceNode) {
          setSourceNode(node.id())
          // Highlight the source node
          node.addClass('source-node')
        } else {
          // Create the edge
          addEdge({
            fromNodeId: sourceNode,
            toNodeId: node.id(),
          })
          // Reset edge creation mode
          setIsCreatingEdge(false)
          setSourceNode(null)
          // Remove highlight from source node
          cyInstance.current?.nodes().removeClass('source-node')
          rerender()
        }
      }
    })

    // NOTE: We intentionally do NOT persist Cytoscape drag positions back to
    // the store. The store's node x/y represent P&ID bounding-box coordinates
    // (pixel positions on the engineering drawing). Cytoscape uses its own
    // layout coordinate space for the graph preview, and writing those back
    // would corrupt the P&ID detection overlay. Cytoscape manages its own
    // node positions internally — they survive within the session without
    // needing to be stored externally.

    return () => {
      if (cyInstance.current) {
        cyInstance.current.removeAllListeners()
      }
      if (zoomDebounce !== null) {
        window.clearTimeout(zoomDebounce)
        zoomDebounce = null
      }
    }
  }, [
    isCreatingEdge,
    isDeletingEdge,
    sourceNode,
    deleteEdgeById,
    addEdge,
    sourceNodes,
    sourceEdges,
    classRecords,
    readOnly,
    onDrawingConnectorClick,
  ])

  // Separate effect for updating graph data (incremental updates)
  // Strategy: Only add/remove changed nodes/edges to avoid full re-render
  // For large updates (>20 nodes), batch process to prevent UI freezing
  useEffect(() => {
    if (!cyInstance.current) return

    const currentNodeIds = new Set(sourceNodes.keys())
    const currentEdgeIds = new Set(sourceEdges.keys())
    const prevNodeIds = prevStateRef.current.nodeIds
    const prevEdgeIds = prevStateRef.current.edgeIds

    // On component mount/remount, check if Cytoscape is out of sync with store
    // This handles the case where component remounts but store still has data
    const cyNodeIds = new Set(cyInstance.current.nodes().map(n => n.id()))
    const isOutOfSync = currentNodeIds.size > 0 && cyNodeIds.size === 0

    // Calculate what changed (incremental diff)
    // If out of sync, treat all current nodes/edges as needing to be added
    const nodesToAdd = isOutOfSync 
      ? Array.from(currentNodeIds)
      : Array.from(currentNodeIds).filter(id => !prevNodeIds.has(id))
    const nodesToRemove = isOutOfSync
      ? []
      : Array.from(prevNodeIds).filter(id => !currentNodeIds.has(id))
    const edgesToAdd = isOutOfSync
      ? Array.from(currentEdgeIds)
      : Array.from(currentEdgeIds).filter(id => !prevEdgeIds.has(id))
    const edgesToRemove = isOutOfSync
      ? []
      : Array.from(prevEdgeIds).filter(id => !currentEdgeIds.has(id))

    const hasChanges = nodesToAdd.length > 0 || nodesToRemove.length > 0 ||
                       edgesToAdd.length > 0 || edgesToRemove.length > 0

    // Clear initial loading state even if there are no changes (empty graph case)
    if (!hasChanges) {
      if (!hasRenderedInitialData.current) {
        hasRenderedInitialData.current = true
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Try to auto-center even if empty, this ensures proper viewport setup
            if (cyInstance.current && currentNodeIds.size > 0) {
              cyInstance.current.fit(undefined, 50) // 50px padding around the graph
            }
            setIsLoading(false)
          })
        })
      }
      return
    }

    // Cleanup any pending batch processing
    if (batchTimeoutRef.current !== null) {
      clearTimeout(batchTimeoutRef.current)
      batchTimeoutRef.current = null
    }

    const shouldShowLoader = nodesToAdd.length > LARGE_GRAPH_THRESHOLD
    
    if (shouldShowLoader) {
      setIsUpdating(true)
    }

    // Remove deleted elements immediately
    nodesToRemove.forEach(id => {
      cyInstance.current?.$id(id).remove()
    })
    edgesToRemove.forEach(id => {
      cyInstance.current?.$id(id).remove()
    })

    const renderNode = (node: Node) => {
      switch (node.type) {
        case 'symbol': {
          // Always prefer symbol_type (ML detected type) over name when available
          // symbol_type has the actual detected type like "INS_Flow_Controler"
          // Note: symbol_type can be the literal string 'null', not actual null
          const nameToUse = (node.symbol_type && node.symbol_type !== 'null') ? node.symbol_type : node.name
          const { label } = parseName(nameToUse)
          // Manually-classified objects have no ML symbol_type and a generic
          // name ("general"), so surface their PIC001 Type (Level III) — e.g. a
          // traced "Pit" tank — the same way connections show their Level III.
          if ((!node.symbol_type || node.symbol_type === 'null') && node.pnid_level_iii) {
            return node.pnid_level_iii
          }
          return label || node.name
        }
        case 'tag':
          return node.content
        case 'connection':
          // Show the resolved Level III taxonomy entry (e.g. "Logic Data
          // Software Signal") so classified lines surface a label that
          // matches the Type field in the Properties panel.
          return node.pnid_level_iii || ''
        default:
          return ''
      }
    }

    // Prepare nodes and edges
    const newNodes = nodesToAdd
      .map(nodeId => {
        const node = sourceNodes.get(nodeId)
        if (!node) return null

        const label = renderNode(node)

        // Always prefer symbol_type (ML detected type) for icons when available
        // symbol_type has the actual detected type like "INS_Flow_Controler"
        // Note: symbol_type can be the literal string 'null', not actual null
        const effectiveName = node.type === 'symbol'
          ? ((node.symbol_type && node.symbol_type !== 'null') ? node.symbol_type : node.name)
          : ''

        const nodeData: {
          data: { id: string; label: string; name: string; type: string }
          position?: { x: number; y: number }
          classes?: string
        } = {
          data: {
            id: nodeId,
            label,
            name: effectiveName,
            type: node.type,
          },
          classes: [
            node.extraProperties?.disconnected === 'true'
              ? 'disconnected'
              : node.extraProperties?.orphaned === 'true'
                ? 'orphaned'
                : '',
            drawingConnectorClasses(node),
          ]
            .filter(Boolean)
            .join(' '),
        }

        if (node.x !== undefined && node.y !== undefined) {
          nodeData.position = { x: node.x, y: node.y }
        }

        return nodeData
      })
      .filter((node): node is NonNullable<typeof node> => node !== null)

    const newEdges = edgesToAdd
      .map(edgeId => {
        const edge = sourceEdges.get(edgeId)
        if (!edge) return null

        // Skip edges whose source/target nodes are missing
        if (!sourceNodes.has(edge.fromNodeId) || !sourceNodes.has(edge.toNodeId)) {
          return null
        }

        return {
          data: {
            id: edgeId,
            source: edge.fromNodeId,
            target: edge.toNodeId,
            relation: edge.relation || '',
          },
          classes: edge.relation === 'flows_to' ? 'flows-to' : '',
        }
      })
      .filter((edge): edge is NonNullable<typeof edge> => edge !== null)

    // Helper to finalize graph update
    const finalizeUpdate = () => {
      prevStateRef.current = {
        nodeIds: new Set(currentNodeIds),
        edgeIds: new Set(currentEdgeIds),
      }

      // Clear initial loading state after first data render and auto-center the graph
      if (!hasRenderedInitialData.current) {
        hasRenderedInitialData.current = true
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Auto-center and fit the graph to viewport on first load
            if (cyInstance.current && currentNodeIds.size > 0) {
              cyInstance.current.fit(undefined, 50) // 50px padding around the graph
            }
            setIsLoading(false)
          })
        })
      } else if (nodesToAdd.length > 0 && cyInstance.current && currentNodeIds.size > 0) {
        // Also fit when new nodes are added after initial render
        requestAnimationFrame(() => {
          cyInstance.current?.fit(undefined, 50)
        })
      }

      if (shouldShowLoader) {
        requestAnimationFrame(() => {
          setIsUpdating(false)
        })
      }
    }

    // KG preview (external graph) has no P&ID coordinates; without a layout the
    // nodes pile at the origin. Store graphs carry preset coords, so they're
    // left untouched.
    const needsAutoLayout =
      graph !== undefined &&
      newNodes.length > 0 &&
      newNodes.filter((n) => n.position !== undefined).length < newNodes.length * 0.2

    // For large updates: batch process to avoid freezing
    if (shouldShowLoader && newNodes.length > LARGE_GRAPH_THRESHOLD) {
      let currentBatch = 0
      const totalBatches = Math.ceil(newNodes.length / BATCH_SIZE)

      const processBatch = () => {
        if (!cyInstance.current) {
          setIsUpdating(false)
          return
        }

        const start = currentBatch * BATCH_SIZE
        const end = Math.min(start + BATCH_SIZE, newNodes.length)
        const batch = newNodes.slice(start, end)

        // Add batch of nodes
        cyInstance.current.add(batch)
        currentBatch++

        if (currentBatch < totalBatches) {
          // Process next batch after a small delay
          batchTimeoutRef.current = window.setTimeout(processBatch, 0)
        } else {
          // All nodes added, now add edges and run layout
          if (newEdges.length > 0) cyInstance.current.add(newEdges)

          if (needsAutoLayout) {
            runFcoseLayout(cyInstance.current)
          }

          finalizeUpdate()
        }
      }

      // Start batch processing
      requestAnimationFrame(() => {
        requestAnimationFrame(processBatch)
      })
    } else {
      // Small updates: process immediately
      if (newNodes.length > 0) cyInstance.current.add(newNodes)
      if (newEdges.length > 0) cyInstance.current.add(newEdges)

      if (needsAutoLayout) {
        // Positionless external graph (KG preview): always lay out, even when
        // sparse, so the nodes don't pile at the origin.
        runFcoseLayout(cyInstance.current)
      } else if (nodesToAdd.length > 0) {
        // Store graph: only run COSE when there's enough edge connectivity
        // (sparse graphs scatter) and most nodes lack positions.
        const nodeCount = currentNodeIds.size
        const edgeCount = currentEdgeIds.size
        const hasGoodConnectivity = nodeCount > 0 && (edgeCount / nodeCount) > 0.5

        const nodesWithPositions = newNodes.filter(n => n.position !== undefined).length
        const mostNodesHavePositions = nodesWithPositions > newNodes.length * 0.8

        if (hasGoodConnectivity && !mostNodesHavePositions) {
          const layout = cyInstance.current.layout(COSE_LAYOUT_CONFIG)
          layout.run()
        }
      }

      finalizeUpdate()
    }

    // Cleanup function to clear any pending timeouts
    return () => {
      if (batchTimeoutRef.current !== null) {
        clearTimeout(batchTimeoutRef.current)
        batchTimeoutRef.current = null
      }
    }
  }, [sourceNodes, sourceEdges, classRecords, graph])

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      // FS-518: plain white background to match Editor Mode (which draws on a
      // white canvas and never applies its grid-pattern) instead of the dot grid.
      style={isV2 ? { backgroundColor: '#ffffff' } : undefined}
    >
      {/* Loading Overlay */}
      {(isLoading || isUpdating) && (
        <div
          className="absolute inset-0 flex items-center justify-center z-50 bg-white dark:bg-dark-card"
        >
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500" />
          </div>
        </div>
      )}
      
      <div
        ref={cyRef}
        className="absolute inset-0"
        style={{ height: '100%', width: '100%' }}
      />
      {!isExternalSearch && (
        <GraphSearch
          cyInstance={cyInstance.current}
          nodes={sourceNodes}
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
        />
      )}
      {/* Action buttons — hidden in V2 (moved to sub-navbar) */}
      {!isV2 && (
        <div className="absolute top-4 right-4 flex gap-4 z-50">
          <Button
            variant="outline"
            onClick={() => setIsSearchOpen(true)}
            className="flex items-center gap-2 bg-white dark:bg-dark-card"
            title={t('graph_editor:editor.searchNodesTitle', { defaultValue: 'Search nodes (Ctrl+F / Cmd+F)' })}
          >
            <Search className="w-4 h-4" />
            {t('graph_editor:editor.searchButton', { defaultValue: 'Search' })}
          </Button>
          <Button
            variant="outline"
            onClick={downloadGraphImage}
            className="flex items-center gap-2 bg-white dark:bg-dark-card"
          >
            <Download className="w-4 h-4" />
            {t('graph_editor:jsonViewer.download', { defaultValue: 'Download' })}
          </Button>
          {!readOnly && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreatingEdge(!isCreatingEdge)
                  setIsDeletingEdge(false)
                  setSourceNode(null)
                  if (cyInstance.current) {
                    cyInstance.current.nodes().removeClass('source-node')
                  }
                }}
                className={
                  isCreatingEdge
                    ? 'bg-red-500 hover:bg-red-600 text-white border-red-500'
                    : 'bg-white dark:bg-dark-card'
                }
              >
                {isCreatingEdge
                  ? t('data_catalog:graph_editor.controls.cancel_edge_creation')
                  : t('data_catalog:graph_editor.controls.create_edge')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeletingEdge(!isDeletingEdge)
                  setIsCreatingEdge(false)
                }}
                className={
                  isDeletingEdge
                    ? 'bg-red-500 hover:bg-red-600 text-white border-red-500'
                    : 'bg-white dark:bg-dark-card'
                }
              >
                {isDeletingEdge
                  ? t('data_catalog:graph_editor.controls.cancel_edge_deletion')
                  : t('data_catalog:graph_editor.controls.delete_edge')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
